import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { DateRange } from '../components/DateRangeFilter/Dropdown';
import ApiService from 'services/api';
import { ICallStats, IReportCallStatsParams, CallStatus, ICalls } from 'interfaces/Reporting';
import AuthContext from './AuthContext';

interface DashboardStats {
  totalCalls: number;
  previousTotalCalls: number;
  completedCalls: number;
  previousCompletedCalls: number;
  performanceScore: number;
  previousPerformanceScore: number;
  activeCalls: number;
}

interface DashboardContextType {
  primaryStats: DashboardStats | null;
  comparisonStats: DashboardStats | null;
  loading: boolean;
  error: string | null;
  fetchStats: (range: DateRange, target: 'primary' | 'comparison') => Promise<void>;
  setDateRange: (range: DateRange, comparisonRange?: DateRange | null) => void;
}

const INITIAL_STATS: DashboardStats = {
  totalCalls: 0,
  previousTotalCalls: 0,
  completedCalls: 0,
  previousCompletedCalls: 0,
  performanceScore: 0,
  previousPerformanceScore: 0,
  activeCalls: 0,
};

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

export const DashboardProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [primaryStats, setPrimaryStats] = useState<DashboardStats | null>(INITIAL_STATS);
  const [comparisonStats, setComparisonStats] = useState<DashboardStats | null>(INITIAL_STATS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentRange, setCurrentRange] = useState<DateRange | undefined>();
  const [comparisonRange, setComparisonRange] = useState<DateRange | null>(null);
  
  const { data: { authToken }, actions: { isLoggedIn } } = useContext(AuthContext);

  const getCallStats = useCallback(async (params: IReportCallStatsParams): Promise<ICallStats> => {
    if (!isLoggedIn()) {
      throw new Error('You are not logged in. Please log in and try again');
    }

    const response: { data: ICallStats } = await ApiService.get('reports/stats', {
      params,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken?.access_token}`,
      },
    });

    return response.data;
  }, [authToken, isLoggedIn]);

  const getAllCalls = useCallback(async (startDate: string, endDate: string): Promise<ICalls> => {
    if (!isLoggedIn()) {
      throw new Error('You are not logged in. Please log in and try again');
    }

    const response: { data: ICalls } = await ApiService.get('reports', {
      params: {
        start_date: startDate,
        end_date: endDate,
        page: 1,
        size: 100,
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken?.access_token}`,
      },
    });

    return response.data;
  }, [authToken, isLoggedIn]);

  const fetchAllCallPages = useCallback(async (startDate: string, endDate: string): Promise<ICalls['items']> => {
    const firstPage = await getAllCalls(startDate, endDate);
    const totalPages = firstPage.pages;
    const allCalls = [...firstPage.items];

    // Fetch remaining pages in parallel if there are any
    if (totalPages > 1) {
      const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
      const pagePromises = remainingPages.map((page: number) =>
        ApiService.get('reports', {
          params: {
            start_date: startDate,
            end_date: endDate,
            page,
            size: 100,
          },
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken?.access_token}`,
          },
        }).then((response: { data: { items: ICalls['items'] } }) => response.data.items)
      );

      const additionalPages = await Promise.all(pagePromises);
      additionalPages.forEach((items: ICalls['items']) => allCalls.push(...items));
    }

    return allCalls;
  }, [authToken, getAllCalls]);

  const fetchStats = useCallback(async (range: DateRange, target: 'primary' | 'comparison') => {
    if (!range?.from || !range?.to) return;
    try {
      setLoading(true);
      setError(null);
      const statsData = await getCallStats({
        start_date: range.from.toISOString(),
        end_date: range.to.toISOString(),
      });
      
      // Fetch all calls to get more detailed stats
      const allCalls = await fetchAllCallPages(range.from.toISOString(), range.to.toISOString());
      
      // Count calls by resolution type
      const transferredCalls = allCalls.filter((call: any) => call.resolution === CallStatus.TRANSFERRED).length;
      const messageTakenCalls = allCalls.filter((call: any) => call.resolution === CallStatus.MESSAGE_TAKEN).length;
      const ongoingCalls = allCalls.filter((call: any) => call.resolution === 'ONGOING').length;
      
      const calculatePerformanceScore = (stats: ICallStats, transferred: number, messageTaken: number) => {
        const totalCalls = stats.num_call > 0 ? stats.num_call : 1; // Prevent division by zero
        
        // Apply weights to different resolution types
        const COMPLETED_WEIGHT = 1.0;    // 100% weight for completed calls
        const TRANSFERRED_WEIGHT = 0.8;  // 80% weight for transferred calls
        const MESSAGE_WEIGHT = 0.7;      // 70% weight for message taken
        
        // Calculate success rate with weighted contributions
        const weightedSuccessful = 
          (stats.num_complete * COMPLETED_WEIGHT) + 
          (transferred * TRANSFERRED_WEIGHT) + 
          (messageTaken * MESSAGE_WEIGHT);
          
        // Success rate accounts for 80% of total score
        const successRate = (weightedSuccessful / totalCalls) * 80;
        
        // Calculate sentiment score using the compound value from VADER
        // Sentiment directly contributes 0-20% to the final score
        let sentimentScore = 10; // Default to neutral (10/20 within the sentiment portion)
        
        if (stats.sentiment.compound !== undefined) {
          // Formula: (compound + 1) * 10 gives a range of 0-20
          sentimentScore = (stats.sentiment.compound + 1) * 10;
        }
        
        // Sum the two components for final score
        return Math.round(successRate + sentimentScore);
      };
      
      const statsObj: DashboardStats = {
        totalCalls: statsData.num_call,
        previousTotalCalls: 0,
        completedCalls: statsData.num_complete,
        previousCompletedCalls: 0,
        performanceScore: calculatePerformanceScore(statsData, transferredCalls, messageTakenCalls),
        previousPerformanceScore: 0,
        activeCalls: ongoingCalls
      };
      
      if (target === 'primary') setPrimaryStats(statsObj);
      else setComparisonStats(statsObj);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching stats');
    } finally {
      setLoading(false);
    }
  }, [fetchAllCallPages, getCallStats]);

  const setDateRange = useCallback((range: DateRange, comparison?: DateRange | null) => {
    setCurrentRange(range);
    setComparisonRange(comparison || null);
  }, []);

  // Fetch stats whenever the date range changes
  React.useEffect(() => {
    if (currentRange) {
      fetchStats(currentRange, 'primary');
    }
  }, [currentRange, comparisonRange, fetchStats]);

  return (
    <DashboardContext.Provider
      value={{
        primaryStats,
        comparisonStats,
        loading,
        error,
        fetchStats,
        setDateRange,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};

export default DashboardContext; 